import { useEffect } from "react";
import { supabase } from "@/lib/supabase";

interface Props {
  listing: {
    id: string; title: string; description: string; images: string[];
    price: number | string; priceUnit: string; city: string; state: string;
    category: string; ownerName: string;
  };
}

const SITE = "https://www.agroaluga.com.br";

/** Dados estruturados (schema.org) + titulo e descricao da pagina do anuncio, para o Google */
export default function ListingJsonLd({ listing }: Props) {
  useEffect(() => {
    let cancelled = false;
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") ?? null;

    const url = `${SITE}/anuncio/${listing.id}`;
    const desc = (listing.description || "").replace(/\s+/g, " ").trim();
    const price = Number(listing.price) || 0;

    document.title = `${listing.title} em ${listing.city}/${listing.state} | AgroAluga`;
    if (meta && desc) meta.setAttribute("content", desc.slice(0, 155));

    (async () => {
      const { data } = await supabase.from("reviews").select("rating").eq("listing_id", listing.id).eq("reported", false);
      if (cancelled) return;
      const ratings: number[] = (data || []).map((r: { rating: number }) => Number(r.rating)).filter((n: number) => n >= 1 && n <= 5);

      const ld: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: listing.title,
        description: desc || listing.title,
        url,
        image: (listing.images || []).slice(0, 5),
        category: listing.category,
        offers: {
          "@type": "Offer",
          url,
          price: price.toFixed(2),
          priceCurrency: "BRL",
          availability: "https://schema.org/InStock",
          priceSpecification: { "@type": "UnitPriceSpecification", price: price.toFixed(2), priceCurrency: "BRL", unitText: listing.priceUnit },
          areaServed: { "@type": "City", name: `${listing.city}, ${listing.state}` },
          seller: { "@type": "Organization", name: listing.ownerName || "Anunciante AgroAluga" },
        },
      };
      if (ratings.length > 0) {
        ld.aggregateRating = {
          "@type": "AggregateRating",
          ratingValue: (ratings.reduce((a: number, b: number) => a + b, 0) / ratings.length).toFixed(1),
          reviewCount: ratings.length, bestRating: 5, worstRating: 1,
        };
      }
      document.getElementById("ld-listing")?.remove();
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "ld-listing";
      el.text = JSON.stringify(ld);
      document.head.appendChild(el);
    })();

    return () => {
      cancelled = true;
      document.getElementById("ld-listing")?.remove();
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, [listing.id, listing.title, listing.description, listing.price, listing.priceUnit, listing.city, listing.state, listing.ownerName, listing.category, listing.images]);

  return null;
}
